import {EntityCollectionServiceBase, EntityOp} from '@ngrx/data';
import {combineLatest, Observable} from 'rxjs';
import {Page} from '../../../shared/model/core/page.model';
import {filter, map} from 'rxjs/operators';
import {Sort, UrlParameter} from '../../../shared/utils/url.builder';
import {Pageable} from '../../../shared/model/core/pageable.model';
import {EntityCollectionReadOnlyService} from './entityCollectionReadOnlyService';
import {HttpModelConverter} from '../converter/httpModel.converter';
import {MomentAdapter} from '../../../shared/utils/moment.utils';
import {IPaginatedService} from './iPaginatedService';
import {DefaultDataservice} from '../../../store/core/dataservice/default.dataservice';
import {SearchApiModel} from './model/api/searchApi.model';

export abstract class PaginatedEntityCollectionReadOnlyService<T, C extends HttpModelConverter<T>>
    extends EntityCollectionReadOnlyService<T, C>
    implements IPaginatedService<T> {

    // readonly currentPageItemIds: number [] = [];
    readonly page$: Observable<Page<T>> = new Observable();
    pageSize: number;
    private currentPage = 0;
    sort: Sort;
    readonly parameters: UrlParameter[] = [];
    additionalParameters: UrlParameter[] = [];
    private lastCorrelationId;
    private initiated = false;

    protected constructor(serviceName: string,
                          entityCollectionServiceBase: EntityCollectionServiceBase<T>,
                          dataService: DefaultDataservice<T>,
                          converter: C,
                          pageSize: number,
                          sort?: Sort,
                          parameters?: UrlParameter[]) {
        super(serviceName, entityCollectionServiceBase, dataService, converter);
        this.sort = sort;
        this.pageSize = pageSize;
        if (parameters) {
            this.parameters = parameters;
        }

        this.page$ = combineLatest([
            this.entityCollectionServiceBase.entities$,
            this.loading$,
            this.queryManyError$,
            this.entityCollectionServiceBase.selectors$['pages$']
        ]).pipe(
           map(([entities, loading, error, pages]) => {
                const currentPage = pages[this.serviceName];
                if (loading || error || !currentPage) {
                    return null;
                }
                // console.dir(currentPage);
                const ids = currentPage.ids;

                let content: T[] = [];
                if (entities && ids.length > 0) {
                    content = ids
                        .map(id => entities.find((value: any) => this.entityCollectionServiceBase.selectId(value) === id))
                        .filter(entity => !!entity);
                }
                return {
                    ... currentPage,
                    content : this.converter.convertFromArray(content),
                };
            }),
            // tap((page) => // console.logByType['[PaginatedEntityCollectionReadOnlyService] ' + this.serviceName + ' page$', page] ))
        );
        // Refresh pagination when any entity is deleted, added or updated
        this.entityActions$.pipe(
            filter((entityAction) => entityAction.payload.entityOp === EntityOp.SAVE_DELETE_ONE_SUCCESS
                || entityAction.payload.entityOp === EntityOp.SAVE_DELETE_MANY_SUCCESS
                || entityAction.payload.entityOp === EntityOp.SAVE_ADD_ONE_SUCCESS
                || entityAction.payload.entityOp === EntityOp.ADD_ONE

                || entityAction.payload.entityOp === EntityOp.SAVE_ADD_MANY_SUCCESS
                || entityAction.payload.entityOp === EntityOp.ADD_MANY

                // || entityAction.payload.entityOp === EntityOp.SAVE_UPDATE_ONE_SUCCESS
                //  || entityAction.payload.entityOp === EntityOp.UPDATE_ONE

                // || entityAction.payload.entityOp === EntityOp.SAVE_UPDATE_MANY_SUCCESS
                // || entityAction.payload.entityOp === EntityOp.UPDATE_MANY

                || entityAction.payload.entityOp === EntityOp.REMOVE_ALL
                || entityAction.payload.entityOp === EntityOp.REMOVE_ONE
                || entityAction.payload.entityOp === EntityOp.REMOVE_MANY
            ))
            .subscribe(() => {
                if (this.initiated) {
                    this.getPage(this.currentPage);
                }
            });
    }

    firstPage(urlParameters?: UrlParameter[], pageSize?: number, sort?: Sort): Observable<T[]> {
        console.log(['url parameter', urlParameters]);
        this.additionalParameters = urlParameters;
        if (sort) {
            this.sort = sort;
        }
        if (pageSize) {
            this.pageSize = pageSize;
        }

        return this.refresh();
    }

    refresh(): Observable<T[]> {
        return this.getPage( 0);
    }


    getPage(number: number): Observable<T[]> {
        this.initiated = true;
        this.currentPage = number;
        const urlParameters = [];
        if (this.parameters?.length > 0) {
            urlParameters.push(...this.parameters);
        }

        if (this.additionalParameters?.length > 0) {
            urlParameters.push(...this.additionalParameters);
        }

        // // console.logByType['getPage', this.entityCollectionServiceBase, this.entityCollectionServiceBase.entityName]);
        this.lastCorrelationId = this.serviceName + '_' + MomentAdapter.current().time();
        return this.entityCollectionServiceBase.getWithQuery(this.buildQueryParams(Pageable.of(number, this.pageSize), urlParameters, this.sort), {
            tag: this.serviceName,
            correlationId: this.lastCorrelationId
        }).pipe(map((entities) => this.converter.convertFromArray(entities)));
    }

    getApiModel(): Promise<SearchApiModel> {
        // alert(this.dataService.getEntityUrl());
        return this.dataService.httpClient.get<SearchApiModel>(this.dataService.getEntityUrl() + 'api-model/search')
            .toPromise();
    }

    public resolveSortKeyLabel(value: string) {
        return value;
    }

    public cancel() {
        // console.logByType['service cancel ', this.lastCorrelationId]);
        if (this.lastCorrelationId) {
            this.entityCollectionServiceBase.cancel(this.lastCorrelationId);
            this.lastCorrelationId = undefined;
        }
    }

    public findAll(urlParameters?: UrlParameter[], sort?: Sort) {
        this.firstPage(urlParameters, 200, sort);
    }
}
